/**
 * Дверь раздела «Настройки» к настоящему серверу (тикет T20).
 *
 * Как и у Раскладки, это единственное место, знающее про сеть (ADR-0002). Ключ сессии
 * сюда не передаётся: он лежит в закрытой куке и уходит сам вместе с запросом.
 *
 * Адрес говорит «мои» и другого не бывает: чужие Настройки этой двери не назвать —
 * ни Администратору, ни кому-либо ещё.
 */
import type { SettingsAnswer, SettingsGateway, SettingGroups, Settings } from './editor.js';

export type Fetcher = (url: string, init?: RequestInit) => Promise<Response>;

export interface GatewayOptions {
  /** Куда ходить: адрес сервера без косой черты на конце. */
  readonly baseUrl: string;
  readonly spaceSlug: string;
  /** Язык объяснений сервера. Не задан — сервер выберет сам (§ 11). */
  readonly language?: string;
  readonly fetch?: Fetcher;
}

export class GatewayError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'GatewayError';
  }
}

const parse = async (response: Response): Promise<unknown> => {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (response.ok) return payload;

  const said = payload as { error?: unknown; message?: unknown } | null;
  const message = typeof said?.message === 'string' && said.message.trim() ? said.message : null;
  const code = typeof said?.error === 'string' ? said.error : 'unknown';
  if (response.status >= 500) {
    throw new GatewayError('Сервер сейчас не отвечает — попробуйте через минуту', code);
  }
  throw new GatewayError(message ?? 'Сервер не принял правку', code);
};

// Тип выписан отдельно нарочно: без него проверка типов не понимает, что после `wrong()`
// продолжения нет, и требует разбирать ответ, которого уже не будет.
const wrong: () => never = () => {
  throw new GatewayError('Сервер прислал Настройки в непонятном виде', 'bad_response');
};

/**
 * Пары «ключ — значение» и ничего кроме: вложенности в Настройках не бывает, пустого
 * значения — тоже. Ненастроенное приходит с сервера умолчанием, а не пустотой, поэтому
 * пустое значение здесь — не «ничего не выбрано», а признак поломки на той стороне.
 */
const settingsOf = (raw: unknown): Settings => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) wrong();
  const clean: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      clean[key] = value;
      continue;
    }
    // Значение, которого в Настройках быть не может, молча не проглатываем: показать его
    // как «настройка не задана» значило бы соврать человеку про его собственный выбор.
    wrong();
  }
  return clean;
};

const namesOf = (raw: unknown): readonly string[] => {
  if (!Array.isArray(raw)) wrong();
  return raw.map((one) => (typeof one === 'string' ? one : wrong()));
};

const groupsOf = (raw: unknown): SettingGroups => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) wrong();
  const clean: Record<string, readonly string[]> = {};
  for (const [group, keys] of Object.entries(raw as Record<string, unknown>)) {
    clean[group] = namesOf(keys);
  }
  return clean;
};

const answerOf = (raw: unknown): SettingsAnswer => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) wrong();
  const source = raw as Record<string, unknown>;
  return {
    settings: settingsOf(source['settings']),
    ownKeys: namesOf(source['ownKeys']),
    ignoredKeys: namesOf(source['ignoredKeys']),
    groups: groupsOf(source['groups']),
  };
};

export function createHttpSettingsGateway(options: GatewayOptions): SettingsGateway {
  const go = options.fetch ?? ((url, init) => fetch(url, init));
  const url = `${options.baseUrl}/spaces/${encodeURIComponent(options.spaceSlug)}/settings`;
  const withLanguage = options.language
    ? `${url}?lang=${encodeURIComponent(options.language)}`
    : url;

  const request = async (target: string, init?: RequestInit): Promise<unknown> => {
    let response: Response;
    try {
      response = await go(target, { credentials: 'include', ...init });
    } catch {
      throw new GatewayError('Нет связи с сервером — проверьте подключение', 'offline');
    }
    return parse(response);
  };

  return {
    async read() {
      return answerOf(await request(withLanguage));
    },

    async update(patch) {
      return answerOf(
        await request(withLanguage, {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(patch),
        }),
      );
    },

    async reset() {
      return answerOf(await request(withLanguage, { method: 'DELETE' }));
    },
  };
}
